import { mapState } from "vuex";
import ErrorHandler from "@/helpers/handlers/error";

import usersService from "@/services/users";
import serviceHandler from "@/helpers/handlers/service";
import cuid from "cuid";
import AlertDialog from "@/components/ui/AlertDialog";
import { DialogHelper } from "@/helpers/dialog-helper";
import { isDemoError } from "@/helpers/handlers/demoService";
import { LOCALE } from "@/enums/locale";
import { GTM_EVENT_NAME } from "@/enums/gtm-events";
import { LOCATION_IMPORT_STATUS } from "@/enums/gmb-locations-import";
import { LOCAL_STORAGE_KEYS } from "@/enums/local-storage";
import { TIMEOUT } from "@/config/timeouts";
import { concat } from "lodash";
import { googleApiConfig } from "@/config/google-api";

export const googleApiProfiles = {
  data() {
    return {
      loading: false,
      loadingButton: false,
      loadingData: false,
      loadingModalImportButton: false,
      loadingImportStatus: false,

      pickProfileDialog: false,
      pickBusinessAddressesDialog: false,
      deleteBusinessAddressesDialog: false,
      confirmationDialog: false,
      accounts: [],
      selectedAccountProfilesToAdd: [],
      apiBusinessAddresses: {},
      selectedBusinessAddresses: [],
      pickedProfile: null,
      confirmationDialogBody: {
        isError: false,
        title: "",
        text: "",
        textSecondary: null,
        hasCancelButton: false,
        hasDeleteButton: false
      },
      lastImport: null,
      locationsStatusTimeout: null,
      ignoreUnverifiedFacilities: true
    };
  },

  computed: {
    ...mapState({
      selectedLocale: state => state.settings.selectedLocale
    }),
    notImportedLocationsCount() {
      return this.lastImport && this.lastImport.statistics
        ? this.lastImport.statistics.disabled_locations_count +
            this.lastImport.statistics.duplicated_locations_count +
            this.lastImport.statistics.locations_without_address_lines_count +
            this.lastImport.statistics.locations_without_placeid_count +
            this.lastImport.statistics.other_problem_count
        : 0;
    },
    alreadyImportedLocationsCount() {
      return this.lastImport && this.lastImport.statistics
        ? this.lastImport.statistics.imported_from_other_profile_count
        : 0;
    },
    summaryData() {
      return {
        imported:
          this.lastImport && this.lastImport.locations_count
            ? this.lastImport.locations_count
            : 0,
        success:
          this.lastImport && this.lastImport.statistics
            ? this.lastImport.statistics.imported_locations_count
            : 0,
        error: this.notImportedLocationsCount,
        alreadyImported: this.alreadyImportedLocationsCount,
        unverified:
          this.lastImport && this.lastImport.statistics
            ? this.lastImport.statistics.unverified
            : 0
      };
    }
  },
  mounted() {
    this.addGoogleIdentityScript();
  },
  methods: {
    addGoogleIdentityScript() {
      if (document.querySelector("#googleIdentityScript")) return;
      const googleIdentityScript = document.createElement("script");
      googleIdentityScript.setAttribute(
        "src",
        "https://accounts.google.com/gsi/client"
      );
      googleIdentityScript.setAttribute("async", "");
      googleIdentityScript.setAttribute("defer", "");
      googleIdentityScript.setAttribute("id", "googleIdentityScript");
      document.head.appendChild(googleIdentityScript);
    },
    async getGoogleAccounts() {
      this.loading = true;

      if (this.panel && this.panel >= 0) {
        // fold expansion panel if unfolded
        this.panel = null;
      }

      this.accounts = await serviceHandler(
        usersService.getGoogleOAuthAccounts()
      );

      this.setProfiles();

      this.loading = false;
    },

    async getLocationsImportStatus(isWizard = false) {
      this.loadingImportStatus = true;
      const response = await serviceHandler(
        usersService.getLocationsImportStatus()
      );

      await this.$store.dispatch("company/updateCurrentCompany");

      this.lastImport = response.length ? response[0] : null;

      if (
        this.lastImport &&
        this.lastImport.status === LOCATION_IMPORT_STATUS.PENDING
      ) {
        localStorage.setItem(
          LOCAL_STORAGE_KEYS.LOCATIONS_IMPORT_STATUS,
          LOCATION_IMPORT_STATUS.PENDING
        );
        this.locationsStatusTimeout = setTimeout(
          this.getLocationsImportStatus,
          isWizard
            ? TIMEOUT.LOCATIONS_IMPORT_STATUS_UPDATE_INTERVAL_WIZARD
            : TIMEOUT.LOCATIONS_IMPORT_STATUS_UPDATE_INTERVAL,
          isWizard
        );

        if (isWizard) {
          this.confirmationDialogBody.loading = true;

          if (
            this.lastImport &&
            this.lastImport.locations_count &&
            this.lastImport.statistics
          ) {
            this.confirmationDialogBody.textSecondary = this.$t(
              "message.companyProfile.listings.confirmationDialog.importedStatistics",
              {
                current_count: this.lastImport.statistics
                  .imported_locations_count,
                total_count: this.lastImport.locations_count
              }
            );
          }
        }
      } else if (
        this.lastImport &&
        (this.lastImport.status === LOCATION_IMPORT_STATUS.COMPLETED ||
          this.lastImport.status ===
            LOCATION_IMPORT_STATUS.FINISHED_WITH_PROBLEMS)
      ) {
        if (isWizard) {
          this.confirmationDialog = false;
          this.confirmationDialogBody.textSecondary = null;
          this.confirmationDialogBody.loading = false;
          if (this.$route.path.split("/")[1] === "wizard") {
            this.$router.push({ name: "wizard-step-3" });
          }
        } else {
          if (this.locationsStatusTimeout) {
            // update Google accounts if case of finishing import while being on the page and status was refreshing itself
            await this.getGoogleAccounts();
          }
        }

        if (this.locationsStatusTimeout) {
          clearTimeout(this.locationsStatusTimeout);
          this.locationsStatusTimeout = null;
        }

        localStorage.removeItem(LOCAL_STORAGE_KEYS.LOCATIONS_IMPORT_STATUS);
        //refresh intercom to get latest data
        if ("intercomSettings" in window && "Intercom" in window) {
          setTimeout(() => {
            window.Intercom("update", {
              locations_count: this.$store.getters["company/facilitiesCount"]
            });
          }, 2000);
        }
      }
      this.loadingImportStatus = false;
    },

    setProfiles() {
      this.accounts.forEach(account => {
        if (!("gmb_profiles" in account)) return;

        account.selectedAccountProfilesToAdd = this.getProfilesToAdd(
          account.gmb_profiles
        ).map(profile => ({ ...profile, gmb_account_id: account.id }));
        account.profilesAdded = this.getProfilesAdded(
          account.gmb_profiles
        ).map(profile => ({ ...profile, gmb_account_id: account.id }));
        delete account.gmb_profiles;
      });
    },

    handleAddAccountButtonClick() {
      this.loadingData = true;

      this.confirmationDialogBody = {
        isError: false,
        title: this.$t(
          "message.companyProfile.listings.confirmationDialog.preAddAccount.title"
        ),
        text: this.$t(
          "message.companyProfile.listings.confirmationDialog.preAddAccount.description"
        ),
        textSecondary: this.$t(
          "message.companyProfile.listings.confirmationDialog.preAddAccount.descriptionSecondary"
        ),
        imgSource: `businessAddresses/gmb_${this.selectedLocale ||
          LOCALE.PL}.gif`,
        imgAlt: this.$t(
          "message.companyProfile.listings.confirmationDialog.preAddAccount.imgAlt"
        ),
        loading: false,
        hasAddAccountButton: true,
        hasCancelButton: false,
        hasDeleteButton: false,
        hasNextButton: false
      };

      this.confirmationDialog = true;
    },

    async handleAddAccount() {
      this.confirmationDialog = false;
      this.loadingData = true;

      window.google.accounts.oauth2
        .initCodeClient({
          client_id: googleApiConfig.clientId,
          scope: googleApiConfig.scope,
          ux_mode: "popup",
          error_callback(error) {
            ErrorHandler.displayError(error);
          },
          callback: async ({ code }) => {
            try {
              if (code) {
                const { data } = await usersService
                  .postGoogleOAuthAccounts(code)
                  .catch(err => {
                    if (err && err.response && err.response.status === 440) {
                      this.confirmationDialogBody = {
                        isError: true,
                        title: this.$t(
                          "message.companyProfile.listings.confirmationDialog.postAddAccount.error.title"
                        ),
                        text: this.$t(
                          "message.companyProfile.listings.confirmationDialog.postAddAccount.error.description"
                        ),
                        textSecondary: this.$t(
                          "message.companyProfile.listings.confirmationDialog.postAddAccount.error.descriptionSecondary"
                        ),
                        hasCancelButton: false,
                        hasDeleteButton: false,
                        hasNextButton: false
                      };

                      this.confirmationDialog = true;
                    }
                  });

                if (
                  data &&
                  !this.accounts.some(account => account.id === data.id)
                ) {
                  await this.accounts.push(data);
                  this.setProfiles();
                  this.openPickProfileDialog(data.id);
                }
              }
              const addedAccount = this.accounts[this.accounts.length - 1];

              if (!addedAccount) {
                ErrorHandler._showError(this.$t("message.error.noAccounts"));
              }

              // GTM event data collection below
              this.$gtm.trackCustomEvent({
                event: GTM_EVENT_NAME.GMB_ACCOUNT_ADD,
                gmb_account_id: addedAccount ? addedAccount.id : null
              });

              // end GTM event stuff

              this.loadingData = false;
            } catch (error) {
              ErrorHandler.displayError(error);

              this.loadingData = false;
            }
          }
        })
        .requestCode();
    },

    async chooseProfile(profile) {
      this.pickedProfile = profile;
      this.loadingButton = true;
      const data = await serviceHandler(
        usersService.getGoogleProfilePlaces(profile.id)
      );

      this.loadingButton = false;

      this.apiBusinessAddresses = this.createTableData(data);

      this.pickProfileDialog = false;
      this.pickBusinessAddressesDialog = true;
    },

    async openDeleteFacilities(profile) {
      this.pickedProfile = profile;
      this.loadingButton = true;

      const data = await serviceHandler(
        usersService.getGoogleProfilePlaces(profile.id)
      );

      this.apiBusinessAddresses = this.createTableData(data);

      // Create change array to object with business addresses
      const dict = {};

      this.apiBusinessAddresses.items.map(item => {
        return (dict[item.gmb_name] = item);
      });

      // Temporary array with only imported business addresses
      let selected = [];

      for (const item of profile.imported_addresses) {
        selected.push(dict[item.gmb_location_name]);
      }

      this.selectedBusinessAddresses = selected;

      //Clear temporary arr
      selected = [];

      this.loadingButton = false;

      this.deleteBusinessAddressesDialog = true;
    },

    createTableData(data) {
      const headers = [
        {
          text: this.$t("message.common.facility"),
          value: "name",
          filterable: true
        },
        {
          text: this.$t("message.common.address"),
          value: "address",
          filterable: true
        },
        {
          text: this.$t("message.common.statusOnGoogleMaps"),
          value: "status",
          sortable: false
        }
      ];

      let items = [];

      if (data && data.length > 0) {
        items = data.map(item => {
          // Takes statuses from API to display only these, which are truthy
          const truthyStatuses = Object.keys(item.statuses).filter(
            k => !!item.statuses[k]
          );

          const fullAddressParts = [];
          ["street", "city", "postal_code", "country"].forEach(key => {
            if (item.address && Object.keys(item.address).includes(key))
              fullAddressParts.push(item.address[key]);
          });

          return {
            name: item.location_name,
            address: item.address,
            full_address: fullAddressParts.join(", "),
            storeCode: item.store_code,
            status: truthyStatuses,
            locationState: item.location_state,
            id: cuid(),
            gmb_name: item.name,
            isImported: this.checkIsImported(item.name),
            isSelected: this.checkIsImported(item.name) || false,
            isDuplicate: item.statuses.is_duplicate,
            isDisconnected: item.statuses.is_disconnected,
            openingStatus: item.opening_status,
            isNonSelectable:
              item.statuses.is_duplicate || item.statuses.is_disconnected
          };
        });
      }

      return { headers, items };
    },

    checkIsImported(name) {
      if (this.pickedProfile) {
        return !!this.pickedProfile.imported_addresses.find(
          item => item.gmb_location_name === name
        );
      }
      return false;
    },

    async handleImportButtonClick(selectedBusinessAddresses) {
      this.loadingModalImportButton = true;

      // GTM event data collection below
      const addedProfilesIds = concat(
        ...this.accounts.map(account =>
          account.profilesAdded.map(profile => profile.id)
        )
      );

      if (
        this.pickedProfile &&
        this.pickedProfile.id &&
        !addedProfilesIds.includes(this.pickedProfile.id)
      ) {
        // send add profile event only if not added yet
        this.$gtm.trackCustomEvent({
          event: GTM_EVENT_NAME.GMB_ACCOUNT_PROFILE_ADD,
          gmb_account_id:
            this.pickedProfile && this.pickedProfile.gmb_account_id
              ? this.pickedProfile.gmb_account_id
              : null,
          gmb_profile_id: this.pickedProfile ? this.pickedProfile.id : null,
          num_imported_locations: selectedBusinessAddresses
            ? selectedBusinessAddresses.length
            : null
        });
      }

      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.GMB_ACCOUNT_PROFILE_LOCATIONS_EDIT,
        action: "add_locations",
        gmb_account_id:
          this.pickedProfile && this.pickedProfile.gmb_account_id
            ? this.pickedProfile.gmb_account_id
            : null,
        gmb_profile_id: this.pickedProfile ? this.pickedProfile.id : null,
        num_imported_locations: selectedBusinessAddresses
          ? selectedBusinessAddresses.length
          : null
      });
      // end GTM event stuff

      const payload = selectedBusinessAddresses.map(item => {
        return {
          name: item.gmb_name,
          ignore_unverified: this.ignoreUnverifiedFacilities
        };
      });

      try {
        const response = await usersService.postGoogleProfile(
          this.pickedProfile.id,
          payload
        );

        const isWizard = this.$route.name === "wizard-step-2";

        if (isWizard) {
          const title =
            response && response.status === 200
              ? this.$t(
                  "message.companyProfile.listings.confirmationDialog.textImportPending"
                )
              : this.$t(
                  "message.companyProfile.listings.confirmationDialog.importErrorText"
                );
          const message =
            response && response.status === 200
              ? this.$t(
                  "message.companyProfile.listings.confirmationDialog.textImportPendingDescription"
                )
              : "";
          this.confirmationDialogBody = {
            isError: false,
            title,
            text: message,
            textSecondary: null,
            loading: true,
            hasCancelButton: false,
            hasDeleteButton: false,
            hasNextButton: true
          };

          this.confirmationDialog = true;
        }
        this.pickBusinessAddressesDialog = false;

        await this.getLocationsImportStatus(isWizard);
        await this.getGoogleAccounts();

        this.$store.dispatch("company/fetchCompanies");
      } catch (error) {
        if (error && error.response) {
          const { status } = error.response;

          if (status === 429) {
            DialogHelper.showDialog(
              AlertDialog,
              {
                text: this.$t(
                  "message.companyProfile.listings.confirmationDialog.tooManyRequestsErrorText"
                ),
                buttonText: this.$t("message.common.ok")
              },
              []
            );
          } else {
            this.confirmationDialogBody = {
              isError: true,
              title: this.$t(
                "message.companyProfile.listings.confirmationDialog.importErrorTitle"
              ),
              text: this.$t(
                "message.companyProfile.listings.confirmationDialog.importErrorText"
              ),
              hasCancelButton: false,
              hasDeleteButton: false,
              hasNextButton: false
            };

            this.confirmationDialog = true;
          }
        } else if (isDemoError(error)) {
          ErrorHandler.displayError(error);
        } else {
          ErrorHandler.displayDefaultError();
        }

        this.pickBusinessAddressesDialog = false;
      }

      this.loadingModalImportButton = false;
    },

    async handleDeleteImportedButtonClick(selectedBusinessAddresses) {
      this.loadingModalImportButton = true;
      const payload = selectedBusinessAddresses.map(item => {
        return { name: item.gmb_name };
      });

      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.GMB_ACCOUNT_PROFILE_LOCATIONS_EDIT,
        action: "remove_locations",
        gmb_account_id:
          this.pickedProfile && this.pickedProfile.gmb_account_id
            ? this.pickedProfile.gmb_account_id
            : null,
        gmb_profile_id: this.pickedProfile ? this.pickedProfile.id : null,
        num_locations: selectedBusinessAddresses
          ? selectedBusinessAddresses.length
          : null
      });

      try {
        await usersService.deleteGoogleProfilePlaces(
          this.pickedProfile.id,
          payload
        );
        this.confirmationDialogBody = {
          isError: false,
          title: this.$t("message.common.accountDeleted"),
          text: this.$t(
            "message.companyProfile.listings.confirmationDialog.textDelete"
          ),
          hasCancelButton: false,
          hasDeleteButton: false,
          hasNextButton: false
        };

        await this.getGoogleAccounts();
        this.deleteBusinessAddressesDialog = false;
        this.confirmationDialog = true;
        this.$store.dispatch("company/fetchCompanies");
      } catch (error) {
        if (isDemoError(error)) {
          ErrorHandler.displayError(error);
        } else {
          this.confirmationDialogBody = {
            isError: true,
            title: this.$t(
              "message.companyProfile.listings.confirmationDialog.deleteErrorTitle"
            ),
            text: this.$t(
              "message.companyProfile.listings.confirmationDialog.deleteErrorText"
            ),
            hasCancelButton: false,
            hasDeleteButton: false,
            hasNextButton: false
          };
          this.pickBusinessAddressesDialog = false;
          this.confirmationDialog = true;
        }
      }

      this.loadingModalImportButton = false;
    },

    async removeProfile(profileId) {
      this.loading = true;

      try {
        await usersService.deleteGoogleProfile(profileId);
        this.selectedAccountProfilesToAdd = [];
        this.confirmationDialogBody = {
          isError: false,
          title: this.$t("message.common.profileDeleted"),
          text: this.$t(
            "message.companyProfile.listings.confirmationDialog.textProfileDelete"
          ),
          hasCancelButton: false,
          hasDeleteButton: false,
          hasNextButton: false
        };

        await this.getGoogleAccounts();
        this.confirmationDialog = true;
      } catch (error) {
        if (isDemoError(error)) {
          ErrorHandler.displayError(error);
        } else {
          this.confirmationDialogBody = {
            isError: true,
            title: this.$t(
              "message.companyProfile.listings.confirmationDialog.deleteProfileErrorTitle"
            ),
            text: this.$t(
              "message.companyProfile.listings.confirmationDialog.deleteProfileErrorText"
            ),
            hasCancelButton: false,
            hasDeleteButton: false,
            hasNextButton: false
          };
          this.confirmationDialog = true;
        }
      }

      this.loading = false;
    },

    openPickProfileDialog(accountId) {
      const account = this.accounts.find(account => account.id === accountId);

      if (account) {
        this.selectedAccountProfilesToAdd =
          account.selectedAccountProfilesToAdd;
        this.pickProfileDialog = true;
      } else {
        this.$notify({
          group: "default",
          type: "error",
          text: this.$t("message.error.other")
        });
      }
    },

    closePickProfileDialog() {
      this.selectedAccountProfilesToAdd = [];
      this.pickProfileDialog = false;
    },

    openRemoveAccountDialog(id) {
      this.confirmationDialogBody = {
        isError: false,
        title: this.$t(
          "message.companyProfile.listings.confirmationDialog.deleteAccountConfirm"
        ),
        text: "",
        hasCancelButton: true,
        hasDeleteButton: true,
        hasNextButton: false,
        type: "account",
        type_id: id
      };

      this.confirmationDialog = true;
    },

    async removeAccount(accountId) {
      this.loading = true;

      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.GMB_ACCOUNT_REMOVE,
        gmb_account_id: accountId
      });

      try {
        await usersService.deleteGoogleAccount(accountId);
        this.accounts = [];
        this.confirmationDialogBody = {
          isError: false,
          title: this.$t("message.common.accountDeleted"),
          text: this.$t(
            "message.companyProfile.listings.confirmationDialog.textAccountDelete"
          ),
          hasCancelButton: false
        };

        await this.getGoogleAccounts();
        this.confirmationDialog = true;
      } catch (error) {
        if (isDemoError(error)) {
          ErrorHandler.displayError(error);
        } else {
          this.confirmationDialogBody = {
            isError: true,
            title: this.$t(
              "message.companyProfile.listings.confirmationDialog.deleteAccountErrorTitle"
            ),
            text: this.$t(
              "message.companyProfile.listings.confirmationDialog.deleteAccountErrorText"
            ),
            hasCancelButton: false
          };
          this.confirmationDialog = true;
        }
      }

      this.loading = false;
    },

    async onDelete({ type, id }) {
      if (type === "account") {
        this.confirmationDialog = false;
        await this.removeAccount(id);
      }
      if (type === "profile") {
        this.confirmationDialog = false;
        await this.removeProfile(id);
      }
    },

    getProfilesToAdd(profiles) {
      return profiles.filter(
        profile =>
          profile.active_imported_addresses_count === 0 &&
          profile.imported_addresses.length === 0
      );
    },

    getProfilesAdded(profiles) {
      return profiles.filter(
        profile =>
          profile.active_imported_addresses_count > 0 &&
          profile.imported_addresses.length > 0
      );
    }
  }
};
